/**
 * TimeInput element is a logic for a special time handling input field.
 * Input field has no date information, only time of day is handled here.
 * Input is given as a "hh:mm" format where hh stand for hours and mm minutes.
 */
class TimeInput {

  /**
   * @constructor
   * @param {HTMLInputElement} element an element to bind time input logic. This field is going to be hidden and replaced with two input fields that are shown in UI
   * @throws {TypeError} when given element is not an instance of HTMLInputElement
   */
  constructor(element) {

    if( !(element instanceof HTMLInputElement) ) {
      throw TypeError('Given element is not an HTMLInputElement');
    }

    this._element = element;

    this._element.type = 'hidden';

    //Take a id or name from the parent element a prefix for the partial elements.
    this._key = this._element.id || this._element.name;

    this._partials = {
      time_1: this._createPartialElement(`${this._key}_time_1`),
      time_2: this._createPartialElement(`${this._key}_time_2`)
    };

    //Add pseudo element to DOM
    this._element.parentNode.append(this._partials.time_1);
    this._element.parentNode.append(this._partials.time_2);

    this._bind();
  }

  /**
   * Return time as a string, returned value sould be something like '12:30' or 01:13
   * @returns {string} time as a string
   */
  getValue() {
    let value1 = String(this._partials.time_1.value).padStart(2, '0');
    let value2 = String(this._partials.time_2.value).padStart(2, '0');
    return `${value1}:${value2}`;
  }

  /**
   * Bind event listeners to pseudo element
   */
  _bind() {
    this._partials.time_1.addEventListener('input', this._onInput.bind(this));
    this._partials.time_1.addEventListener('blur', this._onBlur.bind(this));
    this._partials.time_1.addEventListener('paste', this._onPaste.bind(this));
    this._partials.time_1.addEventListener('copy', this._onCopy.bind(this));
    this._partials.time_1.addEventListener('keydown', this._onKeyDown.bind(this));
    this._partials.time_2.addEventListener('blur', this._onBlur.bind(this));
    this._partials.time_2.addEventListener('input', this._onInput.bind(this));
    this._partials.time_2.addEventListener('paste', this._onPaste.bind(this));
    this._partials.time_2.addEventListener('copy', this._onCopy.bind(this));
    this._partials.time_2.addEventListener('keydown', this._onKeyDown.bind(this));
  }

  /**
   * Create so called so pseudo element for a time input field
   * @param {string} key is a id or name field or parent element, works as a prefix for pseudo element's ID and name field
   * @return {HTMLInputElement} instance of HTMLInputElement
   */
  _createPartialElement(key) {
    const input = document.createElement('input')
    input.name = key;
    input.id = key;
    input.placeholder = '09';
    input.type = 'text';
    input.setAttribute('pattern', '[0-9]*');
    input.setAttribute('inputmode', 'numeric');
    input.setAttribute('maxlength', 2);
    return input;
  }

  /**
  * Called when information is pasted to input element
  * @param {Event} event event object
  */
  _onPaste(event) {
    event.preventDefault();

    let paste = (event.clipboardData || window.clipboardData).getData('text');

    let pasteArray = [];

    if (paste.indexOf(':') === -1) {
      const paste_1 = paste.slice(0,2);
      const paste_2 = paste.slice(2,4);

      if (paste_1) {
        pasteArray.push(paste_1);
      }

      if (paste_2) {
        pasteArray.push(paste_2);
      }
    } else {
      pasteArray = paste.split(':');
    }

    if (pasteArray[0]) {
      // eslint-disable-next-line no-useless-escape
      this._partials.time_1.value = pasteArray[0].slice(0,2).replace(/\:/g, '').padStart(2, '0');
    }

    if (pasteArray[1]) {
      // eslint-disable-next-line no-useless-escape
      this._partials.time_2.value = pasteArray[1].slice(0,2).replace(/\:/g, '').padStart(2, '0');
    }

    if (pasteArray.length) {
      this._partials.time_2.focus();
      this._partials.time_2.setSelectionRange(this._partials.time_2.value.length, this._partials.time_2.value.length);
    }
  }

  /**
   * Called on a copy event
   * @param {Event} event event object
   */
  _onCopy(event) {
    event.clipboardData.setData('text/plain', this.getValue());
    event.preventDefault();
  }

  /**
  * Called when a user copy paste stuff to one of the input fields
  * @param {Event} event event object
  */
  _onKeyDown(event) {

    const KEYCODE_ENTER = 13;
    const KEYCODE_BACKSPACE = 8;
    const KEYCODE_LEFT_ARROW = 37;
    const KEYCODE_RIGHT_ARROW = 39;

    const keyCode = event.keyCode || event.charCode;

    let selectionStart = event.target.selectionStart;
    let selectionEnd = event.target.selectionEnd;
    let focusedOut = false;

    // Enter pressed
    if (keyCode === KEYCODE_ENTER) {
      event.preventDefault();
    }

    if (event.target.name === this._key + '_time_1') {

      if ( (selectionStart === event.target.value.length && keyCode === KEYCODE_RIGHT_ARROW) || keyCode === KEYCODE_ENTER ) {
        selectionStart = 0;
        selectionEnd = selectionStart;

        if (keyCode === KEYCODE_ENTER) {
          selectionEnd = this._partials.time_2.value.length;
        }

        this._partials.time_2.focus();
        this._partials.time_2.setSelectionRange(selectionStart, selectionEnd);
        focusedOut = true;
      }

    } else if (event.target.name === this._key + '_time_2') {

      // Unfocus with enter
      if (keyCode === KEYCODE_ENTER) {
        this.blur();
      }

      if ( selectionStart === 0 && (keyCode === KEYCODE_LEFT_ARROW || (keyCode === KEYCODE_BACKSPACE && selectionStart === selectionEnd)) ) {
        selectionStart = this._partials.time_1.value.length;
        selectionEnd = selectionStart;

        this._partials.time_1.focus();
        this._partials.time_1.setSelectionRange(selectionStart, selectionEnd);
        focusedOut = true;
      }

    }

    // This prevents cursor skipping when focusing to another input field
    if (focusedOut) {
      event.preventDefault();
      focusedOut = false;
    }
  }

  /**
  * Called when a input element's value changes
  * @param {Event} event event object
  */
  _onInput(event) {
    let selectionStart = event.target.selectionStart;
    let selectionEnd = event.target.selectionEnd;

    const regex = /[^\d]/g; // Check if contains other than numeric characters

    // Remove non-numeric characters
    if (event.target.value && regex.test(event.target.value)) {
      event.target.value = event.target.value.replace(regex, '');
    }

    if (event.target.name === this._key + '_time_1' && event.target.value.length === 2) {
      selectionEnd = this._partials.time_2.value.length;

      this._partials.time_2.focus();
      this._partials.time_2.setSelectionRange(0, selectionEnd);
    } else {
      event.target.setSelectionRange(selectionStart, selectionEnd);
    }

    this._element.value = this.getValue();
  }

  /**
   * Called when a input element has lost focus
   * @param {Event} event event object
   */
  _onBlur(event) {
    if ( event.target.value && event.target.id === this._key + '_time_1' && (parseInt(event.target.value) > 24 || parseInt(event.target.value) < 0) ) {
      event.target.value = parseInt(event.target.value) > 23 ? '23' : '0';
    } else if ( event.target.value && event.target.id === this._key + '_time_2' && (parseInt(event.target.value) > 59 || parseInt(event.target.value) < 0) ) {
      event.target.value = parseInt(event.target.value) > 59 ? '59' : '0';
    }

    if (event.target.value.length === 1) {
      event.target.value = event.target.value.padStart(2, '0');
    }

    this._element.value = this.getValue();
  }

}

export default TimeInput;
